/* ============================================================
   Alle Zeichenketten der Oberfläche, in zwei Sprachen (E17/SPEC §7).

   Warum ein eigenes Wörterbuch und keine i18n-Bibliothek: es gibt genau zwei
   Sprachen, keine Pluralregeln jenseits dessen, was `Intl` ohnehin kann, und
   keine Übersetzerinnen von außen. Eine Bibliothek wäre Gerüst ohne Gebäude.
   Der Gewinn dieser Datei ist ein anderer: jeder Satz, den ein Mensch liest,
   steht an EINER Stelle — und `texte.test.ts` prüft, dass keine Zeile im
   Englischen fehlt.

   Die Bauart:
     `de` ist die Quelle und legt mit `as const` die Form fest.
     `en: Woerterbuch` ist derselbe Baum; fehlt dort ein Schlüssel, ist das
     ein ÜBERSETZERFEHLER, kein Laufzeitproblem.
   Werte sind entweder Zeichenketten oder Funktionen (Platzhalter). Funktionen
   sind Absicht: ein Satz mit Zahl darf in beiden Sprachen eine andere
   Wortstellung haben, und ein „{n} Projekte"-Schema erzwingt die deutsche.

   PHASENNAMEN STEHEN NICHT HIER. Sie kommen aus der Datenbank
   (`Phase.name_de` / `Phase.name_en`, SPEC §7) — ein Admin darf sie umbenennen,
   und eine zweite Liste liefe auseinander.

   Die Sprache lebt in `lp.sprache` (localStorage) UND im Profil
   (`Ich.sprache`). Beim Start gilt der gespeicherte Wert; sobald `GET /me`
   zurück ist, übernimmt `spracheVomServer()` den Profilwert — so ist die
   Sprache auf jedem Gerät dieselbe. Schaltet jemand um, schreibt die
   Kopfleiste beides: hier und über `PATCH /me`.
   ============================================================ */
import { create } from "zustand";
import type { ProjektArt, ProjektTeam, Sprache } from "../api/types";
import { load, save } from "./storage";

export const SPRACHE_KEY = "sprache";
export const SPRACHEN: Sprache[] = ["de", "en"];

const de = {
  marke: { name: "LILASp", produkt: "Projekte" },

  allgemein: {
    laedt: "Wird geladen",
    speichern: "Sichern",
    abbrechen: "Abbrechen",
    schliessen: "Schließen",
    loeschen: "Löschen",
    bearbeiten: "Bearbeiten",
    anlegen: "Anlegen",
    hinzufuegen: "Hinzufügen",
    zurueck: "Zurück",
    weiter: "Weiter",
    ja: "Ja",
    nein: "Nein",
    alle: "Alle",
    keine: "Keine",
    mehr: "Mehr",
    suchen: "Suchen",
    gesichert: "Gesichert.",
    heute: "heute",
    optional: "freiwillig",
    pflicht: "Pflichtfeld",
    meldungKopieren: "Meldung kopieren",
    kopiert: "Kopiert.",
  },

  nav: {
    // §32: „/" ist für Angemeldete die Startseite, die Karte liegt unter /karte.
    start: "Start",
    karte: "Karte",
    projekte: "Projekte",
    abgaben: "Abgaben",
    team: "Team",
    aktuelles: "Aktuelles",
    // Fehlerrunde 01.10.2026, #36: was der Punkt an „Aktuelles" sagt — für
    // Vorleser im Namen des Reiters, oben wie in der Mobilleiste.
    aktuellesUngelesen: "neue Beiträge",
    // §34: zwei Reiter zwischen „Aktuelles" und „Büros". Am Telefon stehen sie im
    // Kontomenü — die Mobilleiste bleibt bei ihren sechs Einträgen.
    zeitung: "Zeitung",
    links: "Links",
    bueros: "Büros",
    suchePlatzhalter: "Projekt, Ort oder Person",
    thema: "Thema",
    themaHell: "Helles Thema",
    themaDunkel: "Dunkles Thema",
    themaGesperrt: "Während des Zeichnens lässt sich das Thema nicht wechseln — der Kartenstil wird dabei getauscht, und die begonnene Grenze ginge verloren.",
    sprache: "Sprache",
    profil: "Profil",
    verwaltung: "Verwaltung",
    leitung: "Leitungsblatt",
    runde: "Runde",
    trefferProjekte: "Projekte",
    trefferPersonen: "Menschen",
    trefferBueros: "Büros",
    trefferOrte: "Orte",
    abmelden: "Abmelden",
    anmelden: "Anmelden",
    konto: "Konto",
    bereiche: "Hauptbereiche",
  },

  anmelden: {
    titel: "Anmelden",
    unterzeile: "Wir schicken einen Code an deine Büroadresse.",
    email: "E-Mail",
    emailPlatzhalter: "vorname@lilasp.de",
    codeSchicken: "Code schicken",
    codeTitel: "Code eingeben",
    codeUnterzeile: (mail: string) => `Wir haben einen sechsstelligen Code an ${mail} geschickt. Er gilt 10 Minuten.`,
    ziffer: (i: number, n: number) => `Ziffer ${i} von ${n}`,
    bleiben: "30 Tage angemeldet bleiben",
    andereAdresse: "Andere Adresse",
    erneut: "Neuen Code schicken",
    erneutIn: (zeit: string) => `Neuen Code in ${zeit}`,
    domains: (d: string[]) => `Nur für Adressen ${d.map((x) => "@" + x).join(" und ")}.`,
    ohneAnmeldung: "Ohne Anmeldung zeigt die Seite die öffentliche Projektkarte.",
    zurKarte: "Zur öffentlichen Karte",
    pruefungFehler: "Die Prüfung von Cloudflare ist fehlgeschlagen. Bitte noch einmal.",
    startFehler: "Der Code ließ sich nicht schicken.",
    codeFehler: "Der Code stimmt nicht.",
  },

  profil: {
    titel: "Profil",
    unterzeile: "Name, Kürzel und Heimatbüro — daraus baut die Karte deinen Avatar.",
    ersteinrichtung: "Noch zwei Angaben, dann geht es los.",
    anzeigename: "Name",
    anzeigenamePlatzhalter: "Vorname Nachname",
    alias: "Alias (freiwillig)",
    aliasHinweis: "Der Name, unter dem dich das Haus kennt. Die Suche trifft ihn mit.",
    aliasPlatzhalter: "z. B. Fietje",
    /** Unter dem Namen auf Personenkarte und Steckbrief. */
    aliasAuch: (alias: string) => `auch: ${alias}`,
    kuerzel: "Kürzel",
    kuerzelHinweis: "Zwei bis drei Zeichen — sie stehen im Avatar, wenn kein Bild da ist.",
    titelFeld: "Titel",
    titelPlatzhalter: "z. B. Landschaftsarchitektin",
    buero: "Heimatbüro",
    bueroHinweis: "Dein Avatar liegt auf der Karte bei diesem Büro.",
    /* Zweitbüros (§14.2). Der Hinweis sagt beides — was bleibt (der Avatar)
       und was dazukommt (die Zugehörigkeit); sonst liest sich das Feld wie
       eine zweite Heimat. */
    weitereBueros: "Weitere Büros",
    weitereBuerosHinweis: "Der Avatar steht am Heimatbüro; hier zusätzlich zugehörig.",
    weitereBuerosHinzu: "+ Büro",
    weitereBuerosKeine: "Nur das Heimatbüro.",
    bild: "Profilbild",
    bildWaehlen: "Bild wählen",
    bildTauschen: "Anderes Bild",
    bildEntfernen: "Bild entfernen",
    zuschnittTitel: "Ausschnitt wählen",
    zuschnittHinweis: "Ziehen zum Verschieben, Regler für die Größe. Es wird quadratisch beschnitten.",
    uebernehmen: "Übernehmen",
    zuGross: "Das Bild ist zu groß (höchstens 8 MB).",
    falscherTyp: "Dieses Bildformat nehmen wir nicht an — JPEG, PNG, WebP oder HEIC.",
    rolle: "Rolle",
    email: "E-Mail",
    steckbrief: "Steckbrief",
    steckbriefHinweis: "Steht auf der Karte, wenn jemand deinen Avatar antippt.",
    // Der Zwecktext. Er steht IM Formular und nicht in einem Rollenkonzept —
    // so verlangt es die Recherche (docs/RECHERCHE-STECKBRIEF.md, Abschnitt e):
    // Zweck eng gefasst, Bewertung ausdrücklich ausgeschlossen, Widerruf mit
    // einem Klick daneben.
    freiwillig: "Alles hier ist freiwillig.",
    zweck:
      "Zweck: Kolleginnen und Kollegen für Fragen und Projekte auffindbar machen. Keine Bewertung, keine Leistungskontrolle, keine Grundlage für Personalentscheidungen — und ein leerer Steckbrief ist kein Mangel.",
    /* Abgaben-Erinnerung je Projekt (SPEC §11.4). */
    erinnerungen: "Erinnerungen",
    erinnerungenHinweis:
      "Zu diesen Projekten kommt morgens eine Mail, wenn eine Abgabe ansteht oder überfällig ist. Steht nichts an, kommt auch keine Mail.",
    erinnerungenKeine: "Du stehst in noch keinem Projektteam.",
    erinnerungAn: "Erinnerungen an Abgaben dieses Projekts erhalten",
    erinnerungAus: "Erinnerungen an Abgaben dieses Projekts stumm",
    erinnerungFremdAn: (name: string) => `${name} bekommt Erinnerungen zu diesem Projekt`,
    erinnerungFremdAus: (name: string) => `${name} bekommt keine Erinnerungen zu diesem Projekt`,
    leeren: "Steckbrief leeren",
    leerenHinweis: "Löscht alle sieben Angaben auf einmal. Name, Kürzel, Büro und Bild bleiben.",
    geleert: "Steckbrief geleert.",
    faehigkeiten: "Fähigkeiten",
    faehigkeitenHinweis: "Schlagworte, mit Enter oder Komma trennen — höchstens 20.",
    faehigkeitenPlatzhalter: "z. B. Spielplatzplanung, Wettbewerbe, Pflanzplanung",
    software: "Software",
    softwareHinweis: "Womit du arbeitest — ohne Stufen, ohne Sterne. Höchstens 20.",
    softwarePlatzhalter: "z. B. QGIS, Vectorworks, InDesign",
    sprachen: "Sprachen",
    sprachenHinweis: "Ohne Niveau — „Englisch“ genügt. Höchstens 10.",
    sprachenPlatzhalter: "z. B. Deutsch, Englisch",
    gerade: "Woran ich gerade sitze",
    geradeHinweis: "Ein Satz, der veraltet — und genau deshalb gepflegt wird.",
    geradePlatzhalter: "z. B. Entwurf für den Schulhof in Cuxhaven",
    fragMich: "Frag mich zu …",
    fragMichHinweis: "Bis zu drei Einladungen. Kein Rating, sondern ein offenes Ohr.",
    fragMichPlatzhalter: "z. B. Entwässerungsplanung",
    referenzen: "Referenzprojekte",
    referenzenHinweis: "Bis zu drei Projekte aus dem Haus — deine Arbeit sagt mehr als ein Schlagwort.",
    referenzenSuche: "Projekt suchen",
    referenzenLeer: "Kein Projekt gefunden.",
    referenzenVoll: "Drei sind das Höchste. Nimm eines weg, um ein anderes zu wählen.",
    ueber: "Über mich",
    ueberPlatzhalter: "Zwei, drei Sätze: Schwerpunkte, Lieblingsprojekte, was man dich fragen kann.",
    vorschlaege: "Vorschläge",
    angaben: "Angaben",
    angabenHinweis: "Name, Alias, Kürzel und Titel. Den Steckbrief pflegt jede Person selbst.",
    zeichen: (n: number, max: number) => `${n} / ${max}`,
    /** Barrierefreier Name des Chips: „Alle mit Software QGIS zeigen". */
    hervorheben: (gruppe: string, wort: string) => `Alle mit ${gruppe} „${wort}“ zeigen`,
    hervorhebenAus: "Hervorhebung lösen",

    /* Das Kalender-Abo (§39.8, E39.4). Ein eigener Block und nicht zwanzig
       `kalender…`-Schlüssel zwischen den Steckbriefsätzen: die Karte liest
       nur ihn (`t.profil.kalender`). Die Sätze der Tafel stehen in §39.13
       wörtlich; die Meldungen tragen den Schlusspunkt des Hauses. */
    kalender: {
      titel: "Kalender abonnieren",
      hinweis:
        "Deine Termine, Abgaben und offenen Schritte als Abo in Outlook, Apple- oder Google-Kalender. Der Link ist persönlich: wer ihn hat, sieht diese Einträge.",
      /** Name der Schaltergruppe für Vorlesehilfen — sichtbar sind nur die drei Zeilen. */
      inhalt: "Was im Kalender steht",
      termine: "Termine",
      termineUnter: "aus deinen Projekten und alle, zu denen du gehst",
      abgaben: "Abgaben",
      abgabenUnter: "deiner Projekte und ihre Fristen nach Abnahme, ganztägig",
      schritte: "Nächste Schritte",
      schritteUnter:
        "aus der Runde und aus Terminen, für die du verantwortlich bist — ganztägig am Fälligkeitstag",
      alleAus: "Alle drei aus: der Kalender bleibt leer.",
      erzeugen: "Link erzeugen",
      neuErzeugen: "Neuen Link erzeugen",
      neuFrage: "Der bisherige Link hört sofort auf zu funktionieren. Abos damit musst du neu einrichten.",
      /** Barrierefreier Name des Felds mit der Adresse. */
      adresse: "Dein Kalenderlink",
      einmal: "Diesen Link siehst du nur jetzt. Trag ihn gleich in deinen Kalender ein – sonst erzeugst du später einen neuen.",
      kopieren: "Kopieren",
      kopiert: "Link kopiert.",
      /* Kein Rückfall mit der Adresse in der Meldung (anders als bei den
         Ansichten): sie soll an genau einer Stelle stehen — im Feld darüber,
         das beim Fehlschlag markiert wird. */
      kopierenFehler: "Kopieren ging nicht – der Link im Feld ist markiert, kopier ihn von Hand.",
      eingerichtet: (datum: string) => `Abo eingerichtet am ${datum}`,
      /** `wann` aus `relativZeit`: „vor 2 Stunden", „gestern". */
      zuletzt: (wann: string) => `zuletzt abgerufen ${wann}`,
      /** Unter einer Minute — „zuletzt abgerufen jetzt" klänge nach einem Zähler. */
      zuletztEben: "gerade eben abgerufen",
      /** Ab 14 Tagen nennt `relativZeit` ein Datum, und das braucht ein „am". */
      zuletztAm: (datum: string) => `zuletzt abgerufen am ${datum}`,
      nieAbgerufen: "noch nie abgerufen",
      beenden: "Abo beenden",
      beendenFrage: "Der Link hört sofort auf zu funktionieren.",
      beendet: "Abo beendet.",
      ladeFehler: "Dein Kalender-Abo ließ sich gerade nicht laden.",
      outlookHinweis: "Outlook holt Abos teils erst nach Stunden neu – ein eben angelegter Termin erscheint dort nicht sofort.",
      anleitung: "So richtest du das Abo ein",
      outlook: "Outlook: Kalender → Kalender hinzufügen → Aus dem Internet abonnieren → Link einfügen.",
      apple: "Apple: in der Kalender-App Ablage → Neues Kalenderabonnement → Link einfügen.",
      google: "Google: im Browser unter „Weitere Kalender“ → + → Per URL → Link einfügen.",
    },
  },

  rolle: {
    admin: "Verwaltung",
    // 007: dieselben Rechte wie die Verwaltung, aber ein eigener Name — wer im
    // Haus die Geschäftsführung ist, soll nicht als „Verwaltung" dastehen.
    geschaeftsfuehrung: "Geschäftsführung",
    projektleitung: "Projektleitung",
    mitarbeit: "Mitarbeit",
  },

  projektrolle: {
    geschaeftsfuehrung: "Geschäftsführung",
    leitung: "Projektleitung",
    bearbeitung: "Bearbeitung",
    bauleitung: "Bauleitung",
    unterstuetzung: "Unterstützung",
  },

  // Die ZWEI Projektarten (007). Die Schlüssel sind `ProjektArt` aus
  // api/types.ts; die Namen stehen HIER und nicht in den Wörterbüchern der
  // einzelnen Bildschirme, weil Karte, Projektblatt und Brett dieselbe Art
  // gleich nennen müssen. `projektArtName()` weiter unten ist der Weg dorthin.
  projektArt: {
    konzept: "Konzept",
    objektplanung: "Objektplanung",
  },

  // Die vier Teams des Hauses (007). Bis zum Nachtrag II hießen sie
  // „Projektart" und standen in derselben Spalte — die Namen sind deshalb
  // unverändert, nur die Frage dahinter ist eine andere: das Team sagt, WER
  // plant, die Projektart sagt, WIE gearbeitet wird. `projektTeamName()` unten.
  projektTeam: {
    stadtplanung: "Stadtplanung",
    freiraumentwicklung: "Freiraumentwicklung",
    wettbewerb: "Wettbewerb",
    landschaftsarchitektur: "Landschaftsarchitektur",
  },

  abgabeStatus: {
    geplant: "Geplant",
    in_arbeit: "In Arbeit",
    abgegeben: "Abgegeben",
    verspaetet: "Verspätet",
  },

  tage: {
    heute: "heute",
    in: (n: number) => `in ${n} T`,
    vor: (n: number) => `vor ${n} T`,
    morgen: "morgen",
    gestern: "gestern",
  },

  karte: {
    titel: "Karte",
    hinweisAuswahl: "Projekt oder Person antippen, um die Verbindungen zu zeigen",
    hinweisZiehen: "Avatar ziehen: die Verbindungen federn mit und schnappen zurück.",
    filter: "Filter",
    meine: "Meine",
    baldFaellig: "Abgabe ≤ 7 T",
    oeffentlich: "Öffentlich",
    phasen: "Phasen",
    bueros: "Büros",
    arten: "Projektart",
    teams: "Team",
    naechsteAbgaben: "Nächste Abgaben",
    zeitleiste: "Zeitleiste",
    gelaende: "Gelände",
    luftbild: "Luftbild (Orthophotos der Länder, basemap.at)",
    gebaeude: "3D-Ansicht (Gebäude, Neigung)",
    norden: "Nach Norden ausrichten",
    hineinzoomen: "Hineinzoomen",
    herauszoomen: "Herauszoomen",
    neigung: (grad: number) => `Neigung ${grad}°`,
    oeffnen: "Öffnen",
    grenze: "Grenze",
    keineTreffer: "Kein Projekt passt zu diesen Filtern.",
    schaufensterTitel: "Wo wir gerade planen.",
    schaufensterText: "Freiräume, Spielplätze und Ufer zwischen Nordsee und Donau. Drei Büros, eine Karte.",
    musterHinweis:
      "Demo mit Beispieldaten: Die gezeigten Projekte, Orte und Bilder sind erfunden und dienen nur der Vorführung. Sie stellen keine tatsächlichen Aufträge von LILASp dar.",
    projekteZahl: (n: number) => (n === 1 ? "1 Projekt" : `${n} Projekte`),
  },

  projekt: {
    neu: "Neues Projekt",
    name: "Name",
    nummer: "Projektnummer",
    ort: "Ort",
    land: "Land",
    adresse: "Adresse",
    auftraggeber: "Auftraggeber",
    beschreibung: "Beschreibung",
    phase: "Leistungsphase",
    phasen: "Phasen",
    hauptphase: "Startphase",
    team: "Team",
    bueros: "Büros",
    federfuehrend: "federführend",
    abgaben: "Abgaben",
    galerie: "Galerie",
    grenze: "Projektgrenze",
    flaeche: "Fläche",
    oeffentlichZeigen: "Auf der öffentlichen Karte zeigen",
    titelbild: "Titelbild",
    geloescht: "Projekt gelöscht.",
    loeschenFrage: "Dieses Projekt löschen?",
    loeschenHinweis: "Es verschwindet aus allen Ansichten. Die Daten bleiben im Hintergrund erhalten.",
    konflikt: "Jemand anderes hat dieses Projekt inzwischen geändert. Wir haben den neuen Stand geladen — bitte noch einmal ansehen.",
  },

  board: {
    titel: "Projekte",
    zusammenfassung: (p: number, b: number) => `${p} aktiv · ${b} Büros`,
    alleBueros: "Alle Büros",
    hierherZiehen: "hierher ziehen",
    leer: "Noch kein Projekt in dieser Phase.",
  },

  abgaben: {
    titel: "Abgaben",
    neu: "Abgabe",
    titelFeld: "Titel",
    faellig: "Fällig",
    status: "Status",
    verantwortlich: "Verantwortlich",
    notiz: "Notiz",
    dieseWoche: "Diese Woche",
    naechsteWoche: "Nächste Woche",
    spaeter: "Später",
    vorbei: "Vorbei",
    inDenNaechsten7: (n: number) => `${n} in den nächsten 7 Tagen`,
    zeitraum: (n: number) => `${n} Wochen`,
    leer: "Keine offenen Abgaben.",
  },

  team: {
    titel: "Team × Projekte",
    zusammenfassung: (p: number, pr: number) => `${p} Personen · ${pr} Projekte`,
    auslastung: "Auslastung",
    projektleitung: "Projektleitung",
    mitarbeit: "Mitarbeit",
    projekteVon: (n: number) => (n === 1 ? "1 Projekt" : `${n} Projekte`),
  },

  buero: {
    titel: "Büros",
    personen: "Personen",
    projekte: "Projekte",
    abgabenBald: "Abgaben ≤ 7 T",
    federfuehrend: "federführend",
    /* Zweitbüros (§14.2). „auch" allein steht als Kennzeichen an einer Zeile der
       Büroseite; die Aufzählung steht auf der Personenkarte hinter dem
       Heimatbüro — „Hamburg · auch Wien". */
    auch: "auch",
    auchBueros: (namen: string[]) => `auch ${namen.join(", ")}`,
    auchHier: "gehört auch zu diesem Büro",
    name: "Name",
    farbe: "Farbe",
    strasse: "Straße",
    plz: "PLZ",
    ort: "Ort",
    land: "Land",
    telefon: "Telefon",
    email: "E-Mail",
    // 007: gesetzt wird sie auf der Büroseite, angezeigt mit Gesicht.
    geschaeftsfuehrung: "Geschäftsführung",
    geschaeftsfuehrungNiemand: "— noch offen —",
    punkt: "Koordinaten",
    lon: "Länge (lon)",
    lat: "Breite (lat)",
    punktHinweis: "Dezimalgrad, WGS 84 — z. B. 9.93836 / 53.54437.",
    sortierung: "Reihenfolge",
    inBenutzung: "Dieses Büro lässt sich nicht löschen, solange Personen oder Projekte darauf zeigen.",
    /* Die Büroseite selbst ist für Admins bearbeitbar (SPEC §11.3). */
    bearbeiten: "Bearbeiten",
    bearbeitenEnde: "Bearbeiten beenden",
    standort: "Standort",
    standortSuchen: "Adresse suchen und Standort setzen",
    standortGesetzt: "Standort übernommen — noch nicht gesichert.",
    nurAdmin: "Diese Angaben ändert die Verwaltung.",
  },

  // Die Forumsbereiche (§16.6). Wie bei den Phasen stehen hier nur die
  // BESCHRIFTUNGEN der Verwaltung — die Namen der Bereiche selbst kommen aus
  // der Datenbank (`bereich.name_de` / `name_en`).
  bereich: {
    titel: "Bereiche",
    schluessel: "Schlüssel",
    schluesselHinweis: "2 bis 24 Kleinbuchstaben, Ziffern oder Bindestriche. Steht in jeder Adresse und lässt sich später nicht mehr ändern.",
    nameDe: "Name (deutsch)",
    nameEn: "Name (englisch)",
    sortierung: "Reihenfolge",
    aktiv: "Aktiv",
    neu: "Bereich anlegen",
    beitraege: (n: number) => (n === 1 ? "1 Beitrag" : `${n} Beiträge`),
    hoch: "Nach oben",
    runter: "Nach unten",
    inaktivHinweis: "Stillgelegte Bereiche stehen in keiner Auswahl mehr. Die Beiträge darin bleiben lesbar.",
    inBenutzung: "Dieser Bereich hat noch Beiträge. Setze ihn auf „inaktiv“, statt ihn zu löschen.",
    loeschenGesperrt: "Solange Beiträge darin stehen, lässt sich der Bereich nicht löschen — lege ihn stattdessen still.",
  },

  /* Dienste des Hauses — gelesen von links.lilasp.de (lib/dienste.ts). */
  dienste: {
    titel: "Dienste des Hauses",
    kurz: "Dienste",
    hinweis: "Alle Werkzeuge und Beteiligungen an einem Ort. Gepflegt wird die Liste auf",
    bereich: "Bereich",
    alle: "Alle",
    vpn: "nur Hausnetz",
    fehler: "Die Liste der Dienste ist gerade nicht erreichbar.",
    zurSeite: "Zu links.lilasp.de",
  },

  /* Funktionswünsche (017, SPEC §21). */
  wunsch: {
    titel: "Ich wünsche mir eine Funktion",
    kurz: "Wünsche",
    hinweis:
      "Was fehlt dir? Reiche es ein, stimme über die Wünsche der anderen ab — was oben steht, geht in die Entwicklungsplanung.",
    neu: "Wunsch einreichen",
    feldTitel: "Worum geht es?",
    feldTitelHinweis: "Ein Satz genügt — bis zu 120 Zeichen.",
    feldText: "Und etwas ausführlicher",
    feldTextHinweis: "Freiwillig. Woran arbeitest du, wenn dir das fehlt?",
    absenden: "Einreichen",
    keine: "Noch kein Wunsch. Deiner wäre der erste.",
    dafuer: "Dafür",
    dagegen: "Dagegen",
    zurueckziehen: "Stimme zurückziehen",
    punkte: (n: number) => (n === 1 ? "1 Punkt" : n + " Punkte"),
    stimmen: (n: number) => (n === 1 ? "1 Stimme" : n + " Stimmen"),
    von: "von",
    antwort: "Antwort des Hauses",
    antwortSetzen: "Antworten",
    // Fehlerrunde 01.10.2026, #4: jemand anderes hat die Antwort geändert, während
    // hier getippt wurde — nichts wird still überschrieben.
    antwortInzwischen:
      "Die Antwort wurde inzwischen von jemand anderem geändert. Dein Text steht noch im Feld — verlasse es noch einmal, um ihn trotzdem zu sichern.",
    // §27: vier Status. Die Schlüssel sind die aus 017, die Wörter die neuen.
    statusNamen: {
      offen: "Offen",
      in_arbeit: "In Bearbeitung",
      umgesetzt: "Abgeschlossen",
      abgelehnt: "Abgelehnt",
    },
    loeschenFrage: "Diesen Wunsch löschen?",
    zuViele: "Heute sind von dir schon viele Wünsche eingegangen — bitte morgen weiter.",
    // §25
    kommtBald: "Kommt bald",
    kommtBaldHinweis: "Daran wird gerade gebaut.",
    inArbeitLang: "In Bearbeitung — kommt bald",
    neuUmgesetzt: "Neu abgeschlossen",
    seit: (d: string) => "seit " + d,
    am: (d: string) => "am " + d,
    imMenue: (n: number) => (n === 1 ? "1 kommt bald" : n + " kommen bald"),
  },

  /* Die Marken auf dem Leistungsregler (015, SPEC §18.2). */
  richtwert: {
    titel: "Richtwerte",
    hinweis:
      "Beschriftete Marken auf dem Leistungsregler, je Leistungsphase. Sie sind ein Vorschlag und keine Schranke — eingegeben wird der Leistungsstand immer als Prozentwert.",
    summenHinweis:
      "Die Zahl ist die Stelle auf dem Regler, nicht der Beitrag eines Schritts: „+ Schnitte“ steht bei 100 % und nicht bei 10 %.",
    bezeichnung: "Bezeichnung",
    prozent: "Bei %",
    sortierung: "Reihenfolge",
    aktiv: "Aktiv",
    phase: "Leistungsphase",
    neu: "Marke anlegen",
    neuIn: (kurz: string) => "Marke in " + kurz,
    keine: "Noch keine Marken.",
    keineInPhase: "— keine Marke —",
    anzahl: (n: number) => (n === 1 ? "1 Marke" : n + " Marken"),
    inaktivHinweis:
      "Stillgelegte Marken werden nicht mehr vorgeschlagen. Wer eine Phase öffnet, in der eine solche Marke saß, sieht sie hier weiter.",
    phaseFest: "Eine Marke bleibt bei ihrer Leistungsphase. Für eine andere Phase legst du eine neue an.",
  },

  /* Projektvorlagen (044, SPEC §50.8–§50.10): ein Gerüst aus Abgaben und
     Meilensteinen. Verwaltung UND Anlegeformular lesen diesen Block — darum
     hier und nicht in screens/projekt/texte.ts. */
  vorlage: {
    titel: "Projektvorlagen",
    hinweis:
      "Ein Gerüst aus Abgaben und Meilensteinen, das beim Anlegen eines Projekts angewendet werden kann. Danach ist alles frei änderbar.",
    neu: "Vorlage anlegen",
    neuName: "Neue Vorlage",
    leer: "Noch keine Vorlagen.",
    name: "Name",
    strang: "Für Strang",
    team: "Für Team",
    alle: "alle",
    sortierung: "Reihenfolge",
    eintraege: "Abgaben und Meilensteine",
    keineEintraege: "Noch keine Einträge.",
    titelFeld: "Titel",
    phase: "Phase",
    phaseFehlt: "gibt es nicht mehr",
    phaseErsatz: "Diese Phase gibt es nicht mehr – die Abgabe bekommt die Startphase des Projekts.",
    phaseStill: "stillgelegt",
    versatz: "Tage ab Start",
    wichtig: "Meilenstein",
    eintragNeu: "Eintrag hinzufügen",
    eintragWeg: "Eintrag entfernen",
    eintragNr: (n: number) => "Eintrag " + n,
    zahl: (n: number, m: number) => (n === 1 ? "1 Abgabe" : n + " Abgaben") + " · " + m + " ★",
    vorschau: "Vorschau mit Start heute",
    speichern: "Speichern",
    verwerfen: "Verwerfen",
    verwerfenFrage: "Deine Änderungen an dieser Vorlage gehen verloren.",
    loeschen: "Vorlage löschen",
    loeschenFrage: "Die Vorlage wird gelöscht. Projekte, die sie schon benutzt haben, behalten ihre Abgaben.",
    gesichert: "Vorlage gesichert.",
    geloescht: "Vorlage gelöscht.",
    // Dieselben Sätze wie der Server (vorlage.py, §50.3) — die Oberfläche sagt
    // sie VOR dem Klick, der Server bleibt der Riegel.
    mangel: {
      name: "Die Vorlage braucht einen Namen.",
      zuViele: "Eine Vorlage trägt höchstens 40 Einträge.",
      titel: "Jeder Eintrag der Vorlage braucht einen Titel.",
      phase: "Jeder Eintrag braucht eine Phase.",
      versatz: "Der Versatz liegt zwischen 0 und 1825 Tagen.",
    },
    anwenden: "Vorlage",
    ohne: "Ohne Vorlage",
    passend: "Passend zu Strang und Team",
    andere: "Weitere Vorlagen",
    start: "Start",
    startAusserhalb: "Das Startdatum liegt außerhalb des möglichen Bereichs.",
    zusammen: (n: number, m: number) =>
      n === 0
        ? "legt keine Abgabe an"
        : "legt " +
          (n === 1 ? "1 Abgabe" : n + " Abgaben") +
          " an" +
          (m === 0 ? "" : m === 1 ? ", davon 1 Meilenstein" : ", davon " + m + " Meilensteine"),
    alleZeigen: (n: number) => "alle " + n + " anzeigen",
    weniger: "weniger anzeigen",
    entfaellt: "entfällt – liegt vor heute",
    ausgelassen: (k: number) =>
      k === 1 ? "1 Eintrag liegt vor heute und entfällt." : k + " Einträge liegen vor heute und entfallen.",
    angelegt: (n: number, name: string) =>
      "Angelegt – mit " + (n === 1 ? "1 Abgabe" : n + " Abgaben") + " aus der Vorlage „" + name + "“.",
  },

  phase: {
    titel: "Phasen",
    schluessel: "Schlüssel",
    kurz: "Kurzform",
    nameDe: "Name (deutsch)",
    nameEn: "Name (englisch)",
    farbe: "Farbe",
    prozent: "Anteil in %",
    sortierung: "Reihenfolge",
    aktiv: "Aktiv",
    gruppe: "Gruppe",
    // Die Reiter heißen nach dem, was in der Liste steht, und nicht nach der
    // Projektart: „Objektplanungsphasen" sagt niemand, „Leistungsphasen" jeder.
    konzeptphasen: "Konzeptphasen",
    leistungsphasen: "Leistungsphasen",
    inaktivHinweis: "Inaktive Phasen stehen in keiner Auswahl mehr, bleiben aber an bestehenden Projekten.",
    inBenutzung: "Diese Phase ist in Benutzung. Setze sie auf „inaktiv“, statt sie zu löschen.",
  },

  nutzer: {
    titel: "Nutzerinnen und Nutzer",
    neu: "Person anlegen",
    email: "E-Mail",
    emailHinweis: "Muss auf eine erlaubte Hausdomäne enden.",
    aktiv: "Aktiv",
    inaktiv: "Inaktiv",
    nieAngemeldet: "noch nie angemeldet",
    letzteAnmeldung: "Zuletzt angemeldet",
    entsperren: "Entsperren",
    entsperrt: "Wieder freigegeben.",
    letzterAdmin: "Die letzte Person mit Verwaltungsrechten kann nicht zurückgestuft werden.",
  },

  galerie: {
    titel: "Galerie",
    hochladen: "Hochladen",
    fotoAufnehmen: "Foto aufnehmen",
    hierherZiehen: "Dateien hierher ziehen",
    oeffentlich: "öffentlich",
    oeffentlicheZahl: (n: number) => `${n} öffentlich`,
    alsTitelbild: "Als Titelbild",
    bildTitel: "Bildunterschrift",
    aufgenommen: "Aufgenommen",
    wartet: "wird verarbeitet …",
    fehler: "Verarbeitung fehlgeschlagen",
    mitStandort: (n: number) => (n === 1 ? "1 Foto mit Standort" : `${n} Fotos mit Standort`),
    gpsHinweis: "GPS aus EXIF gelesen, in der Datei entfernt",
    leer: "Noch keine Bilder.",
  },

  grenze: {
    zeichnen: "Zeichnen",
    bearbeiten: "Bearbeiten",
    punktLoeschen: "Punkt löschen",
    sichern: "Grenze sichern",
    verwerfen: "Verwerfen",
    entfernen: "Grenze entfernen",
    punkte: (n: number) => `${n} Punkte`,
    hochladen: "Datei hochladen",
    formate: "GeoPackage, Shapefile (ZIP), GeoJSON, KML/KMZ oder GML",
    kbs: "Koordinatensystem",
    kbsErkannt: "erkannt",
    kbsAusDatei: "aus der Datei",
    kbsGewaehlt: "gewählt",
    kbsUnbekannt: "Koordinatensystem nicht erkannt",
    kbsWaehlen: "Koordinatensystem wählen",
    flaechenWaehlen: "Welche Flächen gehören dazu?",
    uebernehmen: "Übernehmen",
    keineFlaeche: "In der Datei ist keine Fläche.",
  },

  ortssuche: {
    platzhalter: "Adresse oder Ort suchen",
    keineTreffer: "Kein Treffer.",
    aus: "Die Ortssuche ist gerade nicht verfügbar — tippe stattdessen auf die Karte.",
  },

  fehler: {
    umgefallen: "Hier ist etwas schiefgelaufen.",
    umgefallenHinweis: "Der Bildschirm konnte nicht gezeichnet werden. Die Daten sind unberührt.",
    neuLaden: "Neu laden",
    nochmal: "Noch einmal versuchen",
    nichtGefunden: "Das gibt es nicht (mehr).",
    zurStartseite: "Zur Startseite",
    keinZugriff: "Dazu fehlt dir die Berechtigung.",
  },

  wochenbrief: {
    titel: "Wochenbrief",
    erklaerung:
      "Was die vergangene Woche hervorgebracht hat, als Mail: abgegebene Abgaben, neue Projekte, neue Kolleginnen und Kollegen, die Termine der kommenden Woche und die Fotos der Woche.",
    nochKeinVersand: "Der Versand an das ganze Haus ist noch nicht eingerichtet. Verschicken lässt sich bislang nur eine Testmail an die eigene Adresse.",
    stichtag: "Stichtag",
    stichtagHinweis: "Der Montag, an dem der Brief hinausginge — berichtet wird die Woche davor.",
    vorschau: "Vorschau des Wochenbriefs",
    textfassung: "Textfassung",
    testmail: "Testmail an mich senden",
    gesendet: (an: string) => `Wochenbrief an ${an} verschickt.`,
    fehlgeschlagen: "Die Testmail ging nicht hinaus.",
  },

  // §36: die Freigabe neuer Projekte — nur was MEHRERE Bildschirme teilen
  // (der Chip in Galerie, Board und Karte; Kopfleiste und Mobilleiste). Die
  // Sätze im Projektmodal stehen in `screens/projekt/texte.ts`, die der Karte
  // auf der Startseite in `screens/start/texte.ts`.
  freigabe: {
    // Nachgeschlagen mit `Projekt.freigabe` — eine Tabelle, keine drei Zeilen (§21).
    zustand: { offen: "Freigabe offen", freigegeben: "Freigegeben", abgelehnt: "Abgelehnt" },
    // Seit dem 29.09.2026 sieht eine Einreichung nicht mehr jede Projektleitung im
    // Haus, sondern nur die des Projekts — der Titel nennt, wer es wirklich sieht.
    chipTitel: "Noch nicht freigegeben – sichtbar nur für die anlegende Person, die Projektleitung und die Geschäftsführung.",
    menue: "Freigaben",
    punkt: (n: number) => (n === 1 ? "Eine Freigabe braucht dich" : `${n} Freigaben brauchen dich`),
  },
} as const;

/**
 * Die Form des Wörterbuchs — abgeleitet, damit `en` nichts vergessen kann.
 *
 * Drei Fälle je Eintrag: eine Funktion bleibt eine Funktion mit denselben
 * Argumenten, eine KLEINE TABELLE (`{offen: "Offen", …}`) bleibt eine Tabelle
 * mit denselben Schlüsseln, alles andere ist eine Zeichenkette.
 *
 * Die Tabelle kam mit §21 dazu (`wunsch.statusNamen`): Beschriftungen, die über
 * einen Schlüssel aus der API nachgeschlagen werden, sind in beiden Sprachen
 * dieselbe Menge — und eine Zeile je Status (`statusOffen`, `statusGeplant`, …)
 * wäre die Art Liste, die beim sechsten Status jemand zu ergänzen vergisst.
 *
 * §39.8: seit `profil.kalender` darf ein Unterblock AUCH Funktionen tragen
 * („Abo eingerichtet am …"). Die Regel ist darum rekursiv — `Satz` macht auf
 * jeder Tiefe dasselbe: Funktion bleibt Funktion, Zeichenkette bleibt
 * Zeichenkette, ein Objekt wird Schlüssel für Schlüssel abgebildet. Für die
 * flachen Tabellen von vorher kommt genau dieselbe Form heraus.
 */
type Satz<T> = T extends (...a: infer A) => string
  ? (...a: A) => string
  : T extends string
    ? string
    : { [P in keyof T]: Satz<T[P]> };

export type Woerterbuch = {
  [K in keyof typeof de]: { [S in keyof (typeof de)[K]]: Satz<(typeof de)[K][S]> };
};

const en: Woerterbuch = {
  marke: { name: "LILASp", produkt: "Projects" },

  allgemein: {
    laedt: "Loading",
    speichern: "Save",
    abbrechen: "Cancel",
    schliessen: "Close",
    loeschen: "Delete",
    bearbeiten: "Edit",
    anlegen: "Create",
    hinzufuegen: "Add",
    zurueck: "Back",
    weiter: "Next",
    ja: "Yes",
    nein: "No",
    alle: "All",
    keine: "None",
    mehr: "More",
    suchen: "Search",
    gesichert: "Saved.",
    heute: "today",
    optional: "optional",
    pflicht: "Required",
    meldungKopieren: "Copy message",
    kopiert: "Copied.",
  },

  nav: {
    start: "Home",
    karte: "Map",
    projekte: "Projects",
    abgaben: "Deadlines",
    team: "Team",
    aktuelles: "News",
    aktuellesUngelesen: "new posts",
    zeitung: "Newspaper",
    links: "Links",
    bueros: "Offices",
    suchePlatzhalter: "Project, place or person",
    thema: "Theme",
    themaHell: "Light theme",
    themaDunkel: "Dark theme",
    themaGesperrt: "The theme cannot be switched while drawing — swapping the map style would lose the boundary you started.",
    sprache: "Language",
    profil: "Profile",
    verwaltung: "Admin",
    leitung: "Management board",
    runde: "Round",
    trefferProjekte: "Projects",
    trefferPersonen: "People",
    trefferBueros: "Offices",
    trefferOrte: "Places",
    abmelden: "Sign out",
    anmelden: "Sign in",
    konto: "Account",
    bereiche: "Main sections",
  },

  anmelden: {
    titel: "Sign in",
    unterzeile: "We will send a code to your office address.",
    email: "Email",
    emailPlatzhalter: "firstname@lilasp.de",
    codeSchicken: "Send code",
    codeTitel: "Enter the code",
    codeUnterzeile: (mail: string) => `We sent a six-digit code to ${mail}. It is valid for 10 minutes.`,
    ziffer: (i: number, n: number) => `Digit ${i} of ${n}`,
    bleiben: "Stay signed in for 30 days",
    andereAdresse: "Different address",
    erneut: "Send a new code",
    erneutIn: (zeit: string) => `New code in ${zeit}`,
    domains: (d: string[]) => `Only for addresses ${d.map((x) => "@" + x).join(" and ")}.`,
    ohneAnmeldung: "Without signing in the site shows the public project map.",
    zurKarte: "To the public map",
    pruefungFehler: "The Cloudflare check failed. Please try again.",
    startFehler: "The code could not be sent.",
    codeFehler: "That code is not right.",
  },

  profil: {
    titel: "Profile",
    unterzeile: "Name, initials and home office — the map builds your avatar from these.",
    ersteinrichtung: "Two more details, then you are set.",
    anzeigename: "Name",
    anzeigenamePlatzhalter: "First name Last name",
    alias: "Alias (optional)",
    aliasHinweis: "The name the office knows you by. Search matches it too.",
    aliasPlatzhalter: "e.g. Fietje",
    aliasAuch: (alias: string) => `also: ${alias}`,
    kuerzel: "Initials",
    kuerzelHinweis: "Two or three characters — shown in your avatar when there is no photo.",
    titelFeld: "Job title",
    titelPlatzhalter: "e.g. Landscape architect",
    buero: "Home office",
    bueroHinweis: "Your avatar sits at this office on the map.",
    weitereBueros: "Further offices",
    weitereBuerosHinweis: "Your avatar stays at the home office; here you also belong.",
    weitereBuerosHinzu: "+ Office",
    weitereBuerosKeine: "Home office only.",
    bild: "Profile picture",
    bildWaehlen: "Choose a picture",
    bildTauschen: "Different picture",
    bildEntfernen: "Remove picture",
    zuschnittTitel: "Choose the crop",
    zuschnittHinweis: "Drag to move, use the slider for size. It is cropped square.",
    uebernehmen: "Use this",
    zuGross: "That picture is too large (8 MB at most).",
    falscherTyp: "We do not take this image format — JPEG, PNG, WebP or HEIC.",
    rolle: "Role",
    email: "Email",
    steckbrief: "Profile card",
    steckbriefHinweis: "Shown on the map when someone taps your avatar.",
    freiwillig: "Everything here is voluntary.",
    zweck:
      "Purpose: to make colleagues findable for questions and projects. No assessment, no performance monitoring, no basis for personnel decisions — and an empty profile card is not a shortcoming.",
    erinnerungen: "Reminders",
    erinnerungenHinweis:
      "For these projects a mail arrives in the morning when a milestone is due or overdue. Nothing due, no mail.",
    erinnerungenKeine: "You are not in any project team yet.",
    erinnerungAn: "Receiving reminders about this project's milestones",
    erinnerungAus: "Reminders about this project's milestones are muted",
    erinnerungFremdAn: (name: string) => `${name} receives reminders about this project`,
    erinnerungFremdAus: (name: string) => `${name} receives no reminders about this project`,
    leeren: "Clear profile card",
    leerenHinweis: "Deletes all seven entries at once. Name, initials, office and picture stay.",
    geleert: "Profile card cleared.",
    faehigkeiten: "Skills",
    faehigkeitenHinweis: "Keywords, separated by Enter or comma — up to 20.",
    faehigkeitenPlatzhalter: "e.g. playground design, competitions, planting design",
    software: "Software",
    softwareHinweis: "What you work with — no levels, no stars. Up to 20.",
    softwarePlatzhalter: "e.g. QGIS, Vectorworks, InDesign",
    sprachen: "Languages",
    sprachenHinweis: "No proficiency level — “English” is enough. Up to 10.",
    sprachenPlatzhalter: "e.g. German, English",
    gerade: "What I am working on",
    geradeHinweis: "One sentence that goes out of date — which is why it gets kept up.",
    geradePlatzhalter: "e.g. design for the school yard in Cuxhaven",
    fragMich: "Ask me about …",
    fragMichHinweis: "Up to three invitations. Not a rating, an open ear.",
    fragMichPlatzhalter: "e.g. drainage design",
    referenzen: "Reference projects",
    referenzenHinweis: "Up to three projects from the office — your work says more than a keyword.",
    referenzenSuche: "Search projects",
    referenzenLeer: "No project found.",
    referenzenVoll: "Three is the maximum. Remove one to pick another.",
    ueber: "About me",
    ueberPlatzhalter: "Two or three sentences: focus, favourite projects, what people can ask you.",
    vorschlaege: "Suggestions",
    angaben: "Details",
    angabenHinweis: "Name, alias, initials and job title. The profile card is kept by each person themselves.",
    zeichen: (n: number, max: number) => `${n} / ${max}`,
    hervorheben: (gruppe: string, wort: string) => `Show everyone with ${gruppe} “${wort}”`,
    hervorhebenAus: "Clear highlight",

    kalender: {
      titel: "Subscribe to calendar",
      hinweis:
        "Your appointments, deliverables and open next steps as a subscription in Outlook, Apple or Google Calendar. The link is personal: anyone who has it can see these entries.",
      inhalt: "What the calendar contains",
      termine: "Appointments",
      termineUnter: "from your projects and all you attend",
      abgaben: "Deliverables",
      abgabenUnter: "of your projects and their post-handover time limits, all-day",
      schritte: "Next steps",
      schritteUnter: "from the weekly round and from meetings that you are responsible for — all-day on the due date",
      alleAus: "All three off: the calendar stays empty.",
      erzeugen: "Create link",
      neuErzeugen: "Create new link",
      neuFrage: "The current link stops working immediately. You will need to set up existing subscriptions again.",
      adresse: "Your calendar link",
      einmal: "You only see this link now. Add it to your calendar right away – otherwise you will have to create a new one later.",
      kopieren: "Copy",
      kopiert: "Link copied.",
      kopierenFehler: "Copying did not work – the link in the field is selected, copy it by hand.",
      eingerichtet: (datum: string) => `Subscription set up on ${datum}`,
      zuletzt: (wann: string) => `last fetched ${wann}`,
      zuletztEben: "fetched just now",
      zuletztAm: (datum: string) => `last fetched on ${datum}`,
      nieAbgerufen: "never fetched yet",
      beenden: "End subscription",
      beendenFrage: "The link stops working immediately.",
      beendet: "Subscription ended.",
      ladeFehler: "Your calendar subscription could not be loaded right now.",
      outlookHinweis: "Outlook sometimes refreshes subscriptions only after several hours – a new appointment may not show up there right away.",
      anleitung: "How to set up the subscription",
      outlook: "Outlook: Calendar → Add calendar → Subscribe from web → paste the link.",
      apple: "Apple: in the Calendar app, File → New Calendar Subscription → paste the link.",
      google: "Google: on the web, Other calendars → + → From URL → paste the link.",
    },
  },

  rolle: {
    admin: "Admin",
    geschaeftsfuehrung: "Management",
    projektleitung: "Project lead",
    mitarbeit: "Contributor",
  },

  projektrolle: {
    geschaeftsfuehrung: "Management",
    leitung: "Project lead",
    bearbeitung: "Design",
    bauleitung: "Site supervision",
    unterstuetzung: "Support",
  },

  projektArt: {
    konzept: "Concept",
    objektplanung: "Object planning",
  },

  projektTeam: {
    stadtplanung: "Urban planning",
    freiraumentwicklung: "Open-space development",
    wettbewerb: "Competition",
    landschaftsarchitektur: "Landscape architecture",
  },

  abgabeStatus: {
    geplant: "Planned",
    in_arbeit: "In progress",
    abgegeben: "Delivered",
    verspaetet: "Overdue",
  },

  tage: {
    heute: "today",
    in: (n: number) => `in ${n} d`,
    vor: (n: number) => `${n} d ago`,
    morgen: "tomorrow",
    gestern: "yesterday",
  },

  karte: {
    titel: "Map",
    hinweisAuswahl: "Tap a project or a person to show the links",
    hinweisZiehen: "Drag an avatar: the links stretch with it and spring back.",
    filter: "Filters",
    meine: "Mine",
    baldFaellig: "Due ≤ 7 d",
    oeffentlich: "Public",
    phasen: "Phases",
    bueros: "Offices",
    arten: "Project type",
    teams: "Team",
    naechsteAbgaben: "Next deadlines",
    zeitleiste: "Timeline",
    gelaende: "Terrain",
    luftbild: "Aerial imagery (state orthophotos, basemap.at)",
    gebaeude: "3D view (buildings, tilt)",
    norden: "Face north",
    hineinzoomen: "Zoom in",
    herauszoomen: "Zoom out",
    neigung: (grad: number) => `Pitch ${grad}°`,
    oeffnen: "Open",
    grenze: "Boundary",
    keineTreffer: "No project matches these filters.",
    schaufensterTitel: "Where we are planning.",
    schaufensterText: "Open spaces, playgrounds and riverbanks between the North Sea and the Danube. Three offices, one map.",
    musterHinweis:
      "Demo with sample data: the projects, places and pictures shown are fictitious and for demonstration only. They do not represent actual LILASp commissions.",
    projekteZahl: (n: number) => (n === 1 ? "1 project" : `${n} projects`),
  },

  projekt: {
    neu: "New project",
    name: "Name",
    nummer: "Project number",
    ort: "Place",
    land: "Country",
    adresse: "Address",
    auftraggeber: "Client",
    beschreibung: "Description",
    phase: "Work stage",
    phasen: "Stages",
    hauptphase: "Starting phase",
    team: "Team",
    bueros: "Offices",
    federfuehrend: "lead office",
    abgaben: "Deadlines",
    galerie: "Gallery",
    grenze: "Project boundary",
    flaeche: "Area",
    oeffentlichZeigen: "Show on the public map",
    titelbild: "Cover image",
    geloescht: "Project deleted.",
    loeschenFrage: "Delete this project?",
    loeschenHinweis: "It disappears from every view. The data is kept in the background.",
    konflikt: "Someone else changed this project in the meantime. We loaded the new state — please look again.",
  },

  board: {
    titel: "Projects",
    zusammenfassung: (p: number, b: number) => `${p} active · ${b} offices`,
    alleBueros: "All offices",
    hierherZiehen: "drop here",
    leer: "No project in this stage yet.",
  },

  abgaben: {
    titel: "Deadlines",
    neu: "Deadline",
    titelFeld: "Title",
    faellig: "Due",
    status: "Status",
    verantwortlich: "Responsible",
    notiz: "Note",
    dieseWoche: "This week",
    naechsteWoche: "Next week",
    spaeter: "Later",
    vorbei: "Past",
    inDenNaechsten7: (n: number) => `${n} within the next 7 days`,
    zeitraum: (n: number) => `${n} weeks`,
    leer: "No open deadlines.",
  },

  team: {
    titel: "Team × projects",
    zusammenfassung: (p: number, pr: number) => `${p} people · ${pr} projects`,
    auslastung: "Load",
    projektleitung: "Project lead",
    mitarbeit: "Contributing",
    projekteVon: (n: number) => (n === 1 ? "1 project" : `${n} projects`),
  },

  buero: {
    titel: "Offices",
    personen: "People",
    projekte: "Projects",
    abgabenBald: "Due ≤ 7 d",
    federfuehrend: "lead office",
    auch: "also",
    auchBueros: (namen: string[]) => `also ${namen.join(", ")}`,
    auchHier: "also belongs to this office",
    name: "Name",
    farbe: "Colour",
    strasse: "Street",
    plz: "Postcode",
    ort: "City",
    land: "Country",
    telefon: "Phone",
    email: "Email",
    geschaeftsfuehrung: "Management",
    geschaeftsfuehrungNiemand: "— not set —",
    punkt: "Coordinates",
    lon: "Longitude (lon)",
    lat: "Latitude (lat)",
    punktHinweis: "Decimal degrees, WGS 84 — e.g. 9.93836 / 53.54437.",
    sortierung: "Order",
    inBenutzung: "This office cannot be deleted while people or projects point at it.",
    bearbeiten: "Edit",
    bearbeitenEnde: "Stop editing",
    standort: "Location",
    standortSuchen: "Search an address and set the location",
    standortGesetzt: "Location taken over — not saved yet.",
    nurAdmin: "These details are changed by the administration.",
  },

  bereich: {
    titel: "Areas",
    schluessel: "Key",
    schluesselHinweis: "2 to 24 lowercase letters, digits or hyphens. It appears in every address and cannot be changed later.",
    nameDe: "Name (German)",
    nameEn: "Name (English)",
    sortierung: "Order",
    aktiv: "Active",
    neu: "Add an area",
    beitraege: (n: number) => (n === 1 ? "1 post" : `${n} posts`),
    hoch: "Move up",
    runter: "Move down",
    inaktivHinweis: "Retired areas disappear from every picker. The posts in them stay readable.",
    inBenutzung: "This area still has posts. Set it to inactive instead of deleting it.",
    loeschenGesperrt: "While posts point at it, the area cannot be deleted — retire it instead.",
  },

  dienste: {
    titel: "Office services",
    kurz: "Services",
    hinweis: "Every tool and participation portal in one place. The list is maintained at",
    bereich: "Area",
    alle: "All",
    vpn: "office network only",
    fehler: "The list of services cannot be reached right now.",
    zurSeite: "Go to links.lilasp.de",
  },

  wunsch: {
    titel: "I wish this tool could…",
    kurz: "Wishes",
    hinweis:
      "What is missing? Submit it, vote on what others submitted — what rises to the top goes into the development plan.",
    neu: "Submit a wish",
    feldTitel: "What is it about?",
    feldTitelHinweis: "One sentence is enough — up to 120 characters.",
    feldText: "And in a little more detail",
    feldTextHinweis: "Optional. What are you working on when you miss it?",
    absenden: "Submit",
    keine: "No wishes yet. Yours would be the first.",
    dafuer: "For",
    dagegen: "Against",
    zurueckziehen: "Withdraw vote",
    punkte: (n: number) => (n === 1 ? "1 point" : n + " points"),
    stimmen: (n: number) => (n === 1 ? "1 vote" : n + " votes"),
    von: "by",
    antwort: "The office's answer",
    antwortSetzen: "Answer",
    antwortInzwischen:
      "Someone else changed the answer in the meantime. Your text is still in the field — leave it once more to save it anyway.",
    statusNamen: {
      offen: "Open",
      in_arbeit: "In progress",
      umgesetzt: "Completed",
      abgelehnt: "Declined",
    },
    loeschenFrage: "Delete this wish?",
    zuViele: "Quite a few wishes from you today already — please continue tomorrow.",
    // §25
    kommtBald: "Coming soon",
    kommtBaldHinweis: "Someone is building this right now.",
    inArbeitLang: "In progress — coming soon",
    neuUmgesetzt: "Recently completed",
    seit: (d: string) => "since " + d,
    am: (d: string) => "on " + d,
    imMenue: (n: number) => n + " coming soon",
  },

  richtwert: {
    titel: "Reference marks",
    hinweis:
      "Labelled marks on the progress slider, one set per work stage. They are a suggestion, never a limit — progress is always entered as a percentage.",
    summenHinweis:
      "The number is the PLACE on the slider, not the contribution of a step: “+ sections” sits at 100 %, not at 10 %.",
    bezeichnung: "Label",
    prozent: "At %",
    sortierung: "Order",
    aktiv: "Active",
    phase: "Work stage",
    neu: "Add a mark",
    neuIn: (kurz: string) => "Mark in " + kurz,
    keine: "No marks yet.",
    keineInPhase: "— no mark —",
    anzahl: (n: number) => (n === 1 ? "1 mark" : n + " marks"),
    inaktivHinweis:
      "Retired marks are no longer suggested. Anyone opening a stage that carried one still sees it here.",
    phaseFest: "A mark stays with its work stage. For another stage, add a new one.",
  },

  vorlage: {
    titel: "Project templates",
    hinweis:
      "A scaffold of deliverables and milestones that can be applied when creating a project. Everything stays editable afterwards.",
    neu: "Add template",
    neuName: "New template",
    leer: "No templates yet.",
    name: "Name",
    strang: "For strand",
    team: "For team",
    alle: "all",
    sortierung: "Order",
    eintraege: "Deliverables and milestones",
    keineEintraege: "No entries yet.",
    titelFeld: "Title",
    phase: "Stage",
    phaseFehlt: "no longer exists",
    phaseErsatz: "This stage no longer exists – the deliverable gets the project's starting stage.",
    phaseStill: "retired",
    versatz: "Days from start",
    wichtig: "Milestone",
    eintragNeu: "Add entry",
    eintragWeg: "Remove entry",
    eintragNr: (n: number) => "Entry " + n,
    zahl: (n: number, m: number) => (n === 1 ? "1 deliverable" : n + " deliverables") + " · " + m + " ★",
    vorschau: "Preview starting today",
    speichern: "Save",
    verwerfen: "Discard",
    verwerfenFrage: "Your changes to this template will be lost.",
    loeschen: "Delete template",
    loeschenFrage: "The template will be deleted. Projects that already used it keep their deliverables.",
    gesichert: "Template saved.",
    geloescht: "Template deleted.",
    mangel: {
      name: "The template needs a name.",
      zuViele: "A template holds at most 40 entries.",
      titel: "Every entry of the template needs a title.",
      phase: "Every entry needs a stage.",
      versatz: "The offset lies between 0 and 1825 days.",
    },
    anwenden: "Template",
    ohne: "No template",
    passend: "Matching strand and team",
    andere: "Other templates",
    start: "Start",
    startAusserhalb: "The start date is outside the possible range.",
    zusammen: (n: number, m: number) =>
      n === 0
        ? "creates no deliverables"
        : "creates " +
          (n === 1 ? "1 deliverable" : n + " deliverables") +
          (m === 0 ? "" : m === 1 ? ", 1 of them a milestone" : ", " + m + " of them milestones"),
    alleZeigen: (n: number) => "show all " + n,
    weniger: "show fewer",
    entfaellt: "skipped – before today",
    ausgelassen: (k: number) =>
      k === 1 ? "1 entry lies before today and is skipped." : k + " entries lie before today and are skipped.",
    angelegt: (n: number, name: string) =>
      "Created – with " + (n === 1 ? "1 deliverable" : n + " deliverables") + " from the template “" + name + "”.",
  },

  phase: {
    titel: "Stages",
    schluessel: "Key",
    kurz: "Short form",
    nameDe: "Name (German)",
    nameEn: "Name (English)",
    farbe: "Colour",
    prozent: "Share in %",
    sortierung: "Order",
    aktiv: "Active",
    gruppe: "Group",
    konzeptphasen: "Concept phases",
    leistungsphasen: "Work stages",
    inaktivHinweis: "Inactive stages disappear from the pickers but stay on existing projects.",
    inBenutzung: "This stage is in use. Set it to inactive instead of deleting it.",
  },

  nutzer: {
    titel: "People",
    neu: "Add a person",
    email: "Email",
    emailHinweis: "Must end in an allowed house domain.",
    aktiv: "Active",
    inaktiv: "Inactive",
    nieAngemeldet: "never signed in",
    letzteAnmeldung: "Last signed in",
    entsperren: "Unlock",
    entsperrt: "Unlocked.",
    letzterAdmin: "The last person with admin rights cannot be demoted.",
  },

  galerie: {
    titel: "Gallery",
    hochladen: "Upload",
    fotoAufnehmen: "Take a photo",
    hierherZiehen: "Drop files here",
    oeffentlich: "public",
    oeffentlicheZahl: (n: number) => `${n} public`,
    alsTitelbild: "Use as cover",
    bildTitel: "Caption",
    aufgenommen: "Taken",
    wartet: "processing …",
    fehler: "Processing failed",
    mitStandort: (n: number) => (n === 1 ? "1 photo with a location" : `${n} photos with a location`),
    gpsHinweis: "GPS read from EXIF, removed from the file",
    leer: "No pictures yet.",
  },

  grenze: {
    zeichnen: "Draw",
    bearbeiten: "Edit",
    punktLoeschen: "Delete point",
    sichern: "Save boundary",
    verwerfen: "Discard",
    entfernen: "Remove boundary",
    punkte: (n: number) => `${n} points`,
    hochladen: "Upload a file",
    formate: "GeoPackage, Shapefile (ZIP), GeoJSON, KML/KMZ or GML",
    kbs: "Coordinate system",
    kbsErkannt: "detected",
    kbsAusDatei: "from the file",
    kbsGewaehlt: "chosen",
    kbsUnbekannt: "Coordinate system not recognised",
    kbsWaehlen: "Choose a coordinate system",
    flaechenWaehlen: "Which areas belong to it?",
    uebernehmen: "Use these",
    keineFlaeche: "There is no area in this file.",
  },

  ortssuche: {
    platzhalter: "Search an address or place",
    keineTreffer: "Nothing found.",
    aus: "Place search is unavailable right now — tap the map instead.",
  },

  fehler: {
    umgefallen: "Something went wrong here.",
    umgefallenHinweis: "This screen could not be drawn. Your data is untouched.",
    neuLaden: "Reload",
    nochmal: "Try again",
    nichtGefunden: "That does not exist (any more).",
    zurStartseite: "To the start page",
    keinZugriff: "You do not have permission for that.",
  },

  wochenbrief: {
    titel: "Weekly letter",
    erklaerung:
      "What the past week produced, as an email: deadlines handed in, new projects, new colleagues, next week's dates and the photos of the week.",
    nochKeinVersand: "Sending to the whole practice is not set up yet. For now the only way out is a test mail to your own address.",
    stichtag: "Cut-off",
    stichtagHinweis: "The Monday the letter would go out — the week before it is the one reported.",
    vorschau: "Preview of the weekly letter",
    textfassung: "Plain text",
    testmail: "Send a test mail to me",
    gesendet: (an: string) => `Weekly letter sent to ${an}.`,
    fehlgeschlagen: "The test mail did not go out.",
  },

  freigabe: {
    zustand: { offen: "Awaiting approval", freigegeben: "Approved", abgelehnt: "Rejected" },
    chipTitel: "Not approved yet – only the person who created it, the project lead and the management can see it.",
    menue: "Approvals",
    punkt: (n: number) => (n === 1 ? "One approval needs you" : `${n} approvals need you`),
  },
};

export const WOERTERBUECHER: Record<Sprache, Woerterbuch> = { de, en };

/* ------------------------------------------------------------------ Speicher */

interface SprachZustand {
  sprache: Sprache;
  setzen(s: Sprache): void;
}

function gespeicherteSprache(): Sprache {
  const s = load<string | null>(SPRACHE_KEY, null);
  if (s === "de" || s === "en") return s;
  // Kein Eintrag: die Browsersprache entscheidet EINMAL, danach der Eintrag.
  // Alles, was nicht mit "en" beginnt, ist hier Deutsch — das Haus arbeitet
  // auf Deutsch, Englisch ist die Ausnahme.
  try {
    return navigator.language?.toLowerCase().startsWith("en") ? "en" : "de";
  } catch {
    return "de";
  }
}

const useSprachStore = create<SprachZustand>((set) => ({
  sprache: gespeicherteSprache(),
  setzen(s) {
    save(SPRACHE_KEY, s);
    try {
      document.documentElement.lang = s;
      // F3 und F4 führen eigene Wörterbücher unter ihren Bildschirmen
      // (Dateihoheit, SPEC §8) und lesen `lp.sprache` direkt aus dem
      // localStorage. Ein localStorage-Schreibvorgang löst im EIGENEN Reiter
      // kein `storage`-Ereignis aus — ohne dieses Signal bliebe die
      // Projektansicht deutsch, während die Schale schon englisch ist.
      window.dispatchEvent(new Event("lp:sprache"));
    } catch {
      /* kein Dokument (Test in der Knoten-Umgebung) */
    }
    set({ sprache: s });
  },
}));

/**
 * Die gewählte Sprache setzen. Schreibt localStorage und das `lang`-Attribut
 * des Dokuments (Vorlesehilfen und die Silbentrennung des Browsers lesen es).
 * Das PROFIL schreibt der Aufrufer mit — die Kopfleiste ruft danach
 * `useIchAendern`, weil dieses Modul nichts vom Netz wissen soll.
 */
export function setSprache(s: Sprache): void {
  useSprachStore.getState().setzen(s);
}

/**
 * Den Profilwert übernehmen, sobald `GET /me` zurück ist — aber NUR, wenn er
 * sich unterscheidet. Ohne die Prüfung schriebe jeder Start denselben Wert
 * zurück und löste eine Neuzeichnung aus.
 */
export function spracheVomServer(s: Sprache | null | undefined): void {
  if (!s) return;
  if (useSprachStore.getState().sprache === s) return;
  useSprachStore.getState().setzen(s);
}

/** Die gewählte Sprache, außerhalb von React lesbar. */
export function aktuelleSprache(): Sprache {
  return useSprachStore.getState().sprache;
}

/**
 * Der Hook für die Bildschirme:
 *   const { t, sprache } = useT();
 *   <h1>{t.projekt.neu}</h1>
 *   {t.karte.projekteZahl(13)}
 * `sprache` wird für die Formatierer aus lib/format.ts gebraucht und für
 * `Phase.name_de` / `Phase.name_en`.
 */
export function useT(): { t: Woerterbuch; sprache: Sprache } {
  const sprache = useSprachStore((s) => s.sprache);
  return { t: WOERTERBUECHER[sprache], sprache };
}

/**
 * Nur die gewählte Sprache — für die Bildschirme, die ihr eigenes Wörterbuch
 * mitbringen (F3 unter `screens/projekt/`, F4 unter `screens/ansichten/`).
 * Sie brauchen den Schalter der Schale, nicht deren Sätze.
 */
export function useSprache(): Sprache {
  return useSprachStore((s) => s.sprache);
}

/**
 * Die Bezeichnung einer Projektart in der gewählten Sprache.
 *
 * Anders als bei den Phasen kommt sie NICHT aus der Datenbank: die beiden Arten
 * sind ein CHECK und kein Vorrat, den ein Admin pflegt (007_projektart_team.sql).
 * Darum stehen ihre Namen im Wörterbuch — an einer Stelle, für alle Bildschirme.
 */
export function projektArtName(art: ProjektArt, sprache: Sprache): string {
  return WOERTERBUECHER[sprache].projektArt[art];
}

/**
 * Die Bezeichnung eines Teams (007). Aus demselben Grund im Wörterbuch wie die
 * Projektart — und `null` ist ein gültiger Wert: ein Projekt muss keinem Team
 * gehören, und die Aufrufer sollen dafür nicht jedes Mal selbst einen Strich
 * setzen müssen.
 */
export function projektTeamName(team: ProjektTeam | null, sprache: Sprache): string {
  return team === null ? "—" : WOERTERBUECHER[sprache].projektTeam[team];
}

/**
 * Was auf einer Kachel, einer Karte und einer Brettkarte hinter dem Ort steht:
 * das TEAM, solange eines gesetzt ist — sonst die Projektart.
 *
 * Warum nicht beides: die Zeile hat auf einer 200-px-Karte Platz für ein Wort.
 * Und warum das Team zuerst: im Haus wird ein Projekt beim Team verortet
 * („das ist ein Wettbewerb"), die Art sagt nur, wie es geführt wird — die sieht
 * man ohnehin am Phasenchip bzw. am Fortschrittsbalken daneben.
 */
export function teamOderArtName(p: { straenge: ProjektArt[]; team: ProjektTeam | null }, sprache: Sprache): string {
  // Ohne Team nennen wir die Straenge (§24) — bei zweien beide, mit Mittelpunkt.
  return p.team === null
    ? p.straenge.map((a) => projektArtName(a, sprache)).join(" · ")
    : projektTeamName(p.team, sprache);
}

/** Der Name einer Phase in der gewählten Sprache (SPEC §7). */
export function phasenName(phase: { name_de: string; name_en: string }, sprache: Sprache): string {
  return sprache === "en" ? phase.name_en || phase.name_de : phase.name_de || phase.name_en;
}
